import { ChangeDetectionStrategy, Component, ElementRef, HostListener, inject, input, output, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { catchError, debounceTime, distinctUntilChanged, of, Subject, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuditOption, AuditOptionKind } from './audit.models';
import { AuditService } from './audit.service';

let nextId = 0;

/** Searchable choice of a process, user or table for the audit filters (server-side search, 20 suggestions) */
@Component({
  selector: 'iu-audit-option-picker',
  standalone: true,
  imports: [TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="aup" [class.aup--open]="open()" (focusout)="onFocusOut($event)">
      <label class="aup-label" [attr.for]="inputId">{{ label() }}</label>
      <input
        [id]="inputId"
        class="p-inputtext aup-input"
        type="search"
        role="combobox"
        aria-autocomplete="list"
        autocomplete="off"
        [attr.aria-expanded]="open()"
        [attr.aria-controls]="listId"
        [attr.aria-activedescendant]="open() && active() >= 0 ? listId + '-' + active() : null"
        [placeholder]="placeholder() || ('audit.filters.any' | translate)"
        [value]="text()"
        (focus)="search(text())"
        (input)="onInput($any($event.target).value)"
        (keydown)="onKey($event)" />
      @if (open()) {
        <ul class="aup-list" role="listbox" [id]="listId">
          @if (loading()) {
            <li class="aup-hint" role="presentation">{{ 'audit.state.loading' | translate }}</li>
          } @else if (options().length === 0) {
            <li class="aup-hint" role="presentation">{{ 'audit.filters.noMatch' | translate }}</li>
          }
          @for (o of options(); track o.id; let i = $index) {
            <li
              role="option"
              class="aup-option"
              [id]="listId + '-' + i"
              [class.aup-option--active]="i === active()"
              [attr.aria-selected]="i === active()"
              (mousedown)="$event.preventDefault()"
              (click)="pick(o)">
              <span>{{ o.name }}</span>
              @if (o.hint && o.hint !== o.name) {
                <small>{{ o.hint }}</small>
              }
            </li>
          }
        </ul>
      }
    </div>
  `,
  styleUrl: './audit-option-picker.component.scss',
})
export class AuditOptionPickerComponent {
  readonly kind = input.required<AuditOptionKind>();
  readonly label = input<string>('');
  readonly placeholder = input<string>('');
  /** name of the selected value, shown in the input */
  readonly selectedName = input<string>('');
  readonly picked = output<AuditOption>();

  readonly #service = inject(AuditService);
  readonly #host = inject<ElementRef<HTMLElement>>(ElementRef);
  readonly inputId = `aup-${++nextId}`;
  readonly listId = `${this.inputId}-list`;
  readonly open = signal(false);
  readonly loading = signal(false);
  readonly options = signal<AuditOption[]>([]);
  readonly active = signal(-1);
  readonly #typed = signal<string | null>(null);
  readonly #query$ = new Subject<string>();

  constructor() {
    this.#query$
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        switchMap((q) => {
          this.loading.set(true);
          return this.#service.options(this.kind(), q).pipe(catchError(() => of([] as AuditOption[])));
        }),
        takeUntilDestroyed()
      )
      .subscribe((options) => {
        this.loading.set(false);
        this.options.set(options);
        this.active.set(options.length ? 0 : -1);
      });
  }

  text(): string {
    return this.#typed() ?? this.selectedName() ?? '';
  }

  search(q: string): void {
    this.open.set(true);
    this.#query$.next((q ?? '').trim());
  }

  onInput(value: string): void {
    this.#typed.set(value);
    this.search(value);
  }

  onKey(event: KeyboardEvent): void {
    const n = this.options().length;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!this.open()) this.search(this.text());
      else if (n) this.active.set((this.active() + 1) % n);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (n) this.active.set((this.active() - 1 + n) % n);
    } else if (event.key === 'Enter') {
      const o = this.options()[this.active()];
      if (this.open() && o) {
        event.preventDefault();
        this.pick(o);
      }
    } else if (event.key === 'Escape' && this.open()) {
      event.stopPropagation();
      this.close();
    }
  }

  pick(option: AuditOption): void {
    this.picked.emit(option);
    this.close();
  }

  close(): void {
    this.open.set(false);
    this.#typed.set(null);
  }

  /** Tab out of the picker closes the list */
  onFocusOut(event: FocusEvent): void {
    const next = event.relatedTarget as Node | null;
    if (this.open() && (!next || !this.#host.nativeElement.contains(next))) this.close();
  }

  @HostListener('document:mousedown', ['$event'])
  onOutside(event: MouseEvent): void {
    if (this.open() && !this.#host.nativeElement.contains(event.target as Node)) this.close();
  }
}
